import React from "react";
import { Card, Form, Input, Switch, Tag, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faApple, faGooglePlay } from "@fortawesome/free-brands-svg-icons";
import { faCircleInfo, faLock, faBell } from "@fortawesome/free-solid-svg-icons";

/**
 * Thẻ cấu hình nhắc cập nhật cho MỘT nền tảng, kèm bản xem trước popup.
 *
 * Vì sao có xem trước: admin gõ tiêu đề/nội dung mà không thấy nó hiện ra sao trên
 * máy user thì rất dễ viết câu cụt hoặc quá dài. Khối preview đọc thẳng giá trị
 * đang gõ trong Form (useWatch) nên khớp tuyệt đối với cái sẽ lưu.
 */

export const PLATFORM_META = {
    ios: { label: "iOS", store: "App Store", color: "#0071E3", icon: faApple },
    android: { label: "Android", store: "Google Play", color: "#3DDC84", icon: faGooglePlay },
};

const VERSION_RULE = [
    { required: true, message: "Bắt buộc" },
    { pattern: /^\d+(\.\d+){0,2}$/, message: "Dạng x.y.z (vd 1.2.8)" },
];

/** Popup giả lập — chỉ để admin ước lượng độ dài chữ, không cần giống pixel-perfect. */
function PopupPreview({ title, message, forced }) {
    return (
        <div className="rounded-lg border border-dashed border-line p-3 bg-surface-sunken">
            <div className="text-xs text-content-3 mb-2 flex items-center gap-1.5">
                <FontAwesomeIcon icon={faCircleInfo} />
                User thấy thế này
            </div>
            <div className="rounded-lg border border-line bg-surface-raised px-3 py-2.5 shadow-sm max-w-xs">
                <div className="font-semibold text-sm mb-1">
                    {title || "Đã có phiên bản mới"}
                </div>
                <div className="text-xs text-content-3 mb-2.5">
                    {message || "Cập nhật để trải nghiệm tính năng mới nhất và sửa lỗi."}
                </div>
                <div className="flex gap-2 justify-end">
                    {/* Bản bắt buộc KHÔNG có nút "Để sau" — đây là khác biệt dễ quên nhất. */}
                    {!forced && <span className="text-xs text-content-3 px-2 py-1">Để sau</span>}
                    <span className="text-xs text-white bg-rose-600 rounded px-2 py-1">
                        Cập nhật
                    </span>
                </div>
            </div>
        </div>
    );
}

export default function VersionGatePlatformCard({ platformKey, form }) {
    const meta = PLATFORM_META[platformKey];
    const enabled = Form.useWatch([platformKey, "enabled"], form);
    const title = Form.useWatch([platformKey, "title"], form);
    const msg = Form.useWatch([platformKey, "message"], form);
    const latest = Form.useWatch([platformKey, "latestVersion"], form);
    const min = Form.useWatch([platformKey, "minVersion"], form);

    return (
        <Card
            size="small"
            styles={{ header: { borderBottom: `2px solid ${meta.color}22` } }}
            title={
                <span className="flex items-center gap-2 font-semibold">
                    <FontAwesomeIcon icon={meta.icon} style={{ color: meta.color }} />
                    {meta.label}
                    <span className="font-normal text-xs text-content-3">{meta.store}</span>
                </span>
            }
            extra={
                <Form.Item name={[platformKey, "enabled"]} valuePropName="checked" className="m-0">
                    <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
                </Form.Item>
            }
        >
            {/* Tóm tắt luật đang áp dụng — đọc 1 dòng là hiểu, không phải suy từ 2 ô input */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
                {enabled === false ? (
                    <Tag>Không nhắc gì</Tag>
                ) : (
                    <>
                        <Tooltip title="Thấp hơn bản này bị chặn cứng, không bấm 'Để sau' được">
                            <Tag color="red" icon={<FontAwesomeIcon icon={faLock} className="mr-1" />}>
                                Ép cập nhật dưới {min || "—"}
                            </Tag>
                        </Tooltip>
                        <Tooltip title="Thấp hơn bản này được nhắc nhẹ, vẫn vào app được">
                            <Tag color="blue" icon={<FontAwesomeIcon icon={faBell} className="mr-1" />}>
                                Nhắc dưới {latest || "—"}
                            </Tag>
                        </Tooltip>
                    </>
                )}
            </div>

            <div className="flex gap-3">
                <Form.Item
                    name={[platformKey, "latestVersion"]}
                    label="Latest version (nhắc nhẹ)"
                    rules={VERSION_RULE}
                    className="flex-1 mb-3"
                >
                    <Input placeholder="1.2.8" />
                </Form.Item>
                <Form.Item
                    name={[platformKey, "minVersion"]}
                    label="Min version (bắt buộc)"
                    rules={VERSION_RULE}
                    className="flex-1 mb-3"
                >
                    <Input placeholder="1.0.0" />
                </Form.Item>
            </div>

            <Form.Item
                name={[platformKey, "storeUrl"]}
                label="Link store"
                rules={[
                    { required: true, message: "Bắt buộc" },
                    { type: "url", message: "URL không hợp lệ" },
                ]}
                className="mb-3"
            >
                <Input placeholder={`https://${platformKey === "ios" ? "apps.apple.com" : "play.google.com"}/...`} />
            </Form.Item>

            <Form.Item name={[platformKey, "title"]} label="Tiêu đề popup" className="mb-3">
                <Input placeholder="Đã có phiên bản mới" maxLength={120} />
            </Form.Item>

            <Form.Item name={[platformKey, "message"]} label="Nội dung" className="mb-3">
                <Input.TextArea
                    rows={2}
                    placeholder="Cập nhật để trải nghiệm tính năng mới nhất và sửa lỗi."
                    maxLength={300}
                    showCount
                />
            </Form.Item>

            <PopupPreview title={title} message={msg} forced={false} />
            <div className="text-xs text-content-3 mt-1.5">
                Máy dưới <b>{min || "—"}</b> thấy popup y hệt nhưng <b>không có nút “Để sau”</b>.
            </div>
        </Card>
    );
}
